import AddCircleIcon from "@mui/icons-material/AddCircle";
import { Link, Tooltip, IconButton as IconB } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import * as React from "react";
import { useDispatch } from "react-redux";
import useGet from "../../../services/hooks/useGet";
import usePost from "../../../services/hooks/usePost";
import {
  apiEndpoints,
  notificationActions,
  notificationMessages,
} from "../../../shared/constants/api-constant";
import { GridTable } from "../../../shared/ReusableComponents/GridTable";
import IconButton from "../../../shared/ReusableComponents/IconButton";
import VitalDialog from "../../../shared/ReusableComponents/VitalDialog";
import { DateFormatter } from "../../../shared/utils/DateFormatter";
import { hideLoader, showLoader } from "../../../store/currentPreferencesSlice";
import VitalCharts from "../../student/Vitals/VitalCharts";
import { AppConfigurationHelper } from "../../../services/AppConfigurationHelper";
import { cssVar } from "../../../theme/theme";
import { CommonFunctions } from "../../../shared/utils/CommonFunctions";
import { Constants, HtmlHelpers } from "../../../shared/constants/all-constant";
import { NewOffsetInput } from "../../../shared/ReusableComponents/NewOffsetInput";
import usePut from "../../../services/hooks/usePut";
import useDelete from "../../../services/hooks/useDelete";
import ConfirmDialog from "../../../shared/ReusableComponents/ConfirmDialog";
import { ICONS } from "../../../shared/constants/enums";
import { Heading } from "../../../shared/ReusableComponents";

interface IVitalProps {
  parentId?: number;
  isCurrentUserStudentOrTutor: boolean;
}

export interface IStudentVitalData {
  id?: number;
  bpLow?: number;
  bpHigh?: number;
  pulse?: number;
  respiratoryRate?: number;
  oxygenSaturation?: number;
  temperature?: number;
  updatedOn: string;
}

export interface ISuperUserVitalData extends IStudentVitalData {
  offset: number;
}

interface IStudentVitalFormData extends IStudentVitalData {
  caseId?: number;
  userId?: number;
}
interface ISuperUserVitalFormData extends IStudentVitalFormData {
  offset: number | undefined;
  templateId: number;
}

export const Vital: React.FC<IVitalProps> = ({
  parentId,
  isCurrentUserStudentOrTutor,
}) => {
  const dispatch = useDispatch();
  const [originalData, setOriginalData] = React.useState<
    IStudentVitalData[] | ISuperUserVitalData[]
  >([]);
  const messages = {
    DELETED: "Vitals have been Successfully Deleted.",
    SAVED: "Vitals have been Successfully Saved.",
    UPDATED: "Vitals have been Successfully Updated.",
  };
  const [open, setOpen] = React.useState(false);
  const { error: postError, postService, loading: PostLoading } = usePost();
  const { error: getError, data, getService, loading: GetLoading } = useGet();
  const {
    error: putError,
    data: putResponse,
    putService,
    loading: PutLoading,
  } = usePut();
  const {
    error: deleteError,
    deleteService,
    loading: DeleteLoading,
  } = useDelete();
  const [openConfirmation, setOpenConfirmation] = React.useState(false);

  const initialFormData = {
    id: 0,
    updatedOn: "",
    bpLow: undefined,
    bpHigh: undefined,
    pulse: undefined,
    respiratoryRate: undefined,
    oxygenSaturation: undefined,
    temperature: undefined,
  };

  const [studentVitalFormData, setStudentVitalFormData] =
    React.useState<IStudentVitalFormData>({
      ...initialFormData,
      caseId: parentId,
      userId: 0,
    });
  const [superUserVitalFormData, setSuperUserVitalFormData] =
    React.useState<ISuperUserVitalFormData>({
      ...initialFormData,
      templateId: parentId ?? 0,
      userId: 0,
      offset: undefined,
    });

  const [isTableVisible, setIsTableVisible] = React.useState(true);

  const columns: GridColDef[] = [
    {
      field: "bpLow",
      headerName: "Blood Pressure (mmHg)",
      type: "number",
      renderCell: (params: any) => {
        const { bpLow, bpHigh } = params.row;
        const nameVal = CommonFunctions.getDisplayValue(null); // Will return "-" or "NA"

        if (
          CommonFunctions.getDisplayValue(bpHigh) === nameVal &&
          CommonFunctions.getDisplayValue(bpLow) === nameVal
        ) {
          return <span>{nameVal}</span>;
        }

        return (
          <span>
            {CommonFunctions.getDisplayValue(bpHigh) !== nameVal
              ? `${CommonFunctions.getDisplayValue(bpHigh)}/`
              : `${nameVal}/`}
            {CommonFunctions.getDisplayValue(bpLow)}
          </span>
        );
      },
    },
    {
      field: "pulse",
      headerName: "Heart Rate (per min)",
      valueGetter: (value, row) => CommonFunctions.getDisplayValue(row.pulse),
    },
    {
      field: "respiratoryRate",
      headerName: "Respiratory Rate (per min)",
      valueGetter: (value, row) =>
        CommonFunctions.getDisplayValue(row.respiratoryRate),
    },
    {
      field: "oxygenSaturation",
      headerName: "Oxygen Saturation (%)",
      valueGetter: (value, row) =>
        CommonFunctions.getDisplayValue(row.oxygenSaturation),
    },
    {
      field: "temperature",
      headerName: `Temperature (°${
        AppConfigurationHelper.Instance()?.temperatureUnit
      })`,
      valueGetter: (value, row) =>
        CommonFunctions.getDisplayValue(row.temperature),
    },
  ];

  const fields = [
    { label: "BP Systolic (mmHg)", key: "bpHigh" },
    { label: "BP Diastolic (mmHg)", key: "bpLow" },
    { label: "Heart Rate (per min)", key: "pulse" },
    { label: "Respiratory Rate (per min)", key: "respiratoryRate" },
    { label: "Oxygen Saturation (%)", key: "oxygenSaturation" },
    {
      label: `Temperature (°${
        AppConfigurationHelper.Instance()?.temperatureUnit
      })`,
      key: "temperature",
    },
  ];

  const [selectedIdToDelete, setSelectedIdToDelete] = React.useState(0);

  React.useEffect(() => {
    if (GetLoading || PostLoading) dispatch(showLoader());
    else dispatch(hideLoader());
  }, [GetLoading, PostLoading]);

  const onConfirm = async () => {
    setOpenConfirmation(false);
    if (selectedIdToDelete === 0) return;

    await deleteService(
      `${apiEndpoints.TEMPLATE_VITAL}/${selectedIdToDelete}`,
      null,
      messages.DELETED
    );
    setSelectedIdToDelete(0);
    getService(`${apiEndpoints.TEMPLATE_VITALS}/${parentId}`);
  };

  const deleteVital = async (id: number) => {
    setSelectedIdToDelete(id);
    setOpenConfirmation(true);
  };
  const initiateEditVital = (id: number) => {
    setOpen(true);
    const d = originalData.find((x) => x.id === id) as ISuperUserVitalData;
    setSuperUserVitalFormData({
      id: id,
      bpHigh: d?.bpHigh,
      bpLow: d?.bpLow,
      oxygenSaturation: d?.oxygenSaturation,
      templateId: parentId ?? 0,
      pulse: d?.pulse,
      respiratoryRate: d?.respiratoryRate,
      temperature: d?.temperature,
      userId: 0,
      updatedOn: "",
      offset: d?.offset === 0 ? undefined : d?.offset,
    });
  };

  if (isCurrentUserStudentOrTutor) {
    columns.splice(
      0,
      0,
      {
        field: "updatedOn",
        headerName: "Date",
        valueGetter: (value, row) => DateFormatter.formatDate(value),
      },
      {
        field: "",
        headerName: "Time",
        valueGetter: (value, row) => DateFormatter.formatTime(row.updatedOn),
      }
    );
  } else {
    columns.splice(0, 0, {
      field: "offset",
      headerName: "Offset",
      valueGetter: (value, row) =>
        value === 0
          ? Constants.DEFAULT_OFFSET_VALUE
          : CommonFunctions.convertMinutesToOffset(value),
    });
    if (CommonFunctions.IsCurrentSuperUser()) {
      columns.push({
        field: "",
        headerName: "Action",
        resizable: false,
        renderCell: (params) => {
          return (
            <React.Fragment>
              <IconB onClick={() => initiateEditVital(params.row.id)}>
                <span className="material-symbols-outlined  icon-button">
                  edit
                </span>
              </IconB>
              {HtmlHelpers.FOUR_TIMES_SPACE}
              <IconB onClick={() => deleteVital(params.row.id)}>
                <span className="material-symbols-outlined icon-button">
                  delete
                </span>
              </IconB>
            </React.Fragment>
          );
        },
      });
    }
  }

  const handleNewVitalButtonClick = () => {
    if (isCurrentUserStudentOrTutor) {
      setStudentVitalFormData({
        ...initialFormData,
      });
    } else {
      setSuperUserVitalFormData({
        ...initialFormData,
        offset: undefined,
        id: 0,
        templateId: parentId ?? 0,
      });
    }
    setOpen(true);
  };

  const handleSubmitNewVital = async () => {
    if (isCurrentUserStudentOrTutor) {
      studentVitalFormData.id = undefined;
      studentVitalFormData.updatedOn = new Date().toISOString();
      studentVitalFormData.caseId = parentId;
      await postService(
        apiEndpoints.ADDVITAL,
        studentVitalFormData,
        messages.SAVED
      );
      getService(`${apiEndpoints.VITALS}/${parentId}`);
      setStudentVitalFormData({
        ...initialFormData,
        caseId: parentId,
        userId: 0,
      });
    } else {
      if (superUserVitalFormData.offset === 0 || !parentId) {
        return;
      }
      if (superUserVitalFormData.id && superUserVitalFormData.id > 0) {
        if (!superUserVitalFormData.offset) superUserVitalFormData.offset = 0;
        await putService(
          `${apiEndpoints.TEMPLATE_VITAL}`,
          superUserVitalFormData,
          messages.UPDATED
        );
        getService(`${apiEndpoints.TEMPLATE_VITALS}/${parentId}`);
      } else {
        superUserVitalFormData.templateId = parentId;
        await postService(
          apiEndpoints.TEMPLATE_VITAL,
          superUserVitalFormData,
          messages.SAVED
        );
        getService(`${apiEndpoints.TEMPLATE_VITALS}/${parentId}`);
      }
      setSuperUserVitalFormData({
        ...initialFormData,
        offset: undefined,
        templateId: parentId,
      });
    }
    setOpen(false);
  };

  const handleClose = () => {
    setStudentVitalFormData(initialFormData);
    setOpen(false);
  };

  React.useEffect(() => {
    if (data) {
      let sortedData = [];
      if (!isCurrentUserStudentOrTutor) {
        sortedData = (data as ISuperUserVitalData[])?.sort(
          (a, b) =>
            new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime()
        );
      } else {
        sortedData = (data as IStudentVitalData[]).sort(
          (a, b) =>
            new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime()
        );
      }

      setOriginalData(sortedData);
    } else if (!data && originalData.length === 0) {
      isCurrentUserStudentOrTutor
        ? getService(`${apiEndpoints.VITALS}/${parentId}`)
        : getService(`${apiEndpoints.TEMPLATE_VITALS}/${parentId}`);
    }
  }, [data, parentId]);

  const handleShowTableClick = () => {
    setIsTableVisible(!isTableVisible);
  };
  const chartData = [...originalData]
    ?.sort((x, y) => (new Date(x.updatedOn) < new Date(y.updatedOn) ? -1 : 1))
    ?.map((x) => {
      return {
        updatedOn: DateFormatter.formatDateForCharts(x.updatedOn),
        bpHigh: x.bpHigh,
        bpLow: x.bpLow,
        oxygenSaturation: x.oxygenSaturation,
        pulse: x.pulse,
        respiratoryRate: x.respiratoryRate,
        temperature: x.temperature,
      };
    });

  return (
    <React.Fragment>
      <ConfirmDialog
        text={`${notificationMessages.DELETE_MULTIPLE_RECORD_CONFIRMATION} ${notificationActions.VITALS}?`}
        confirmText="Delete"
        cancelText="Cancel"
        dialogOpen={openConfirmation}
        setDialogOpen={() => setOpenConfirmation(false)}
        handleConfirm={onConfirm}
        icon={ICONS.DELETE}
        className="delete-icon"
        width="xs"
      />
      <div>
        <div className="headerDiv">
          <div className="iconHeading">
            <Heading
              type="h5"
              isBold={true}
              text={isTableVisible ? "Vitals" : ""}
            />
            {(CommonFunctions.IsCurrentSuperUser() ||
              CommonFunctions.IsCurrentUserStudent() ||
              CommonFunctions.IsCurrentUserTutor()) &&
              isTableVisible && (
                <IconButton
                  className="iconBtn"
                  onClick={handleNewVitalButtonClick}
                  text="Add Value"
                  icon={
                    <AddCircleIcon fill="#fff" style={{ fontSize: "20px" }} />
                  }
                />
              )}
          </div>
          {isCurrentUserStudentOrTutor && (
            <div style={{ marginRight: "20px", fontSize: "20px" }}>
              {
                <Link
                  onClick={handleShowTableClick}
                  sx={{
                    cursor: "pointer",
                    color: "info.main",
                    textDecorationColor: cssVar("--color-info-main"),
                  }}
                >
                  <span>{isTableVisible ? "Chart" : "Table"}</span>
                </Link>
              }
            </div>
          )}
        </div>
        {isTableVisible && (
          <React.Fragment>
            <GridTable
              rows={originalData}
              columns={columns}
              pageSizeOptions={[5, 10, 15]}
              initialPageSize={10}
              showSearchBar={false}
            />
            <VitalDialog
              open={open}
              onClose={handleClose}
              onSubmit={handleSubmitNewVital}
              title="Add Value"
              formData={
                isCurrentUserStudentOrTutor
                  ? studentVitalFormData
                  : superUserVitalFormData
              }
              setFormData={
                isCurrentUserStudentOrTutor
                  ? setStudentVitalFormData
                  : setSuperUserVitalFormData
              }
              fields={fields}
              isCurrentUserStudentOrTutor={isCurrentUserStudentOrTutor}
              isEditMode={
                isCurrentUserStudentOrTutor
                  ? studentVitalFormData?.id
                  : superUserVitalFormData?.id
              }
            />
          </React.Fragment>
        )}
        {!isTableVisible && <VitalCharts vitalData={chartData} />}
      </div>
    </React.Fragment>
  );
};
